/**
 * NIGHTBOUND - the creature.
 *
 * A layered compositor:
 *
 *   base body  ->  mutation layers (future)  ->  condition overlays  ->  state FX
 *
 * Artwork is resolved at runtime from the shared manifest, so a new file under
 * `public/assets/creatures/<species>/` is picked up with no code change. When
 * nothing is found the procedural silhouette is used instead.
 */

import { useMemo, type ReactNode } from 'react'
import { STATE_ANIMATION, type CreatureStateId } from '../../shared/creatureState'
import { creatureLayerCandidates } from '../../shared/assets/manifest'
import type { SpeciesDefinition } from '../../shared/species'
import { creatureArtCandidates } from '../art/creatureArt'
import { ProceduralCreature } from '../art/proceduralCreatures'
import { useFirstAvailableAsset } from '../hooks'
import { cx } from './ui'

/** A future mutation layer, e.g. `{ slot: 'eyes', key: 'viper' }`. */
export interface CreatureLayerSpec {
  slot: string
  key: string
}

export interface CreatureRendererProps {
  species: SpeciesDefinition
  state: CreatureStateId
  /** Future mutation layers composited above the body. */
  layers?: CreatureLayerSpec[]
  /** Wired to tap-to-interact. Omit to render a non-interactive portrait. */
  onInteract?: () => void
  interactive?: boolean
  disabled?: boolean
  /** Accessible name for the interactive region. */
  ariaLabel?: string
  className?: string
}

export function CreatureRenderer({
  species,
  state,
  layers = [],
  onInteract,
  interactive = true,
  disabled = false,
  ariaLabel,
  className,
}: CreatureRendererProps) {
  const candidates = useMemo(
    () => creatureArtCandidates(species.assetKey, state),
    [species.assetKey, state],
  )
  const { src } = useFirstAvailableAsset(candidates)
  const animation = STATE_ANIMATION[state]

  const inner = (
    <div className={cx('relative h-full w-full', className)}>
      <div className={cx('nb-creature h-full w-full', animation)}>
        {src ? (
          <img
            src={src}
            alt=""
            aria-hidden="true"
            draggable={false}
            className="h-full w-full select-none object-contain"
          />
        ) : (
          <ProceduralCreature
            speciesId={species.id}
            accent={species.accent}
            state={state}
            className="h-full w-full"
          />
        )}
        {layers.map((layer) => (
          <MutationLayer key={`${layer.slot}:${layer.key}`} speciesKey={species.assetKey} layer={layer} />
        ))}
      </div>
      <CreatureOverlay state={state} />
    </div>
  )

  if (!interactive || !onInteract) {
    return <div className="h-full w-full">{inner}</div>
  }

  return (
    <button
      type="button"
      onClick={onInteract}
      disabled={disabled}
      aria-label={ariaLabel ?? `Interact with ${species.name}`}
      className="group relative block h-full w-full cursor-pointer rounded-[4px] disabled:cursor-default"
    >
      <span className="block h-full w-full transition-transform duration-200 group-active:scale-[0.985]">
        {inner}
      </span>
    </button>
  )
}

function MutationLayer({ speciesKey, layer }: { speciesKey: string; layer: CreatureLayerSpec }) {
  const candidates = useMemo(
    () => creatureLayerCandidates(speciesKey, layer.slot, layer.key),
    [speciesKey, layer.slot, layer.key],
  )
  const { src } = useFirstAvailableAsset(candidates)
  if (!src) return null
  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      draggable={false}
      className="pointer-events-none absolute inset-0 h-full w-full select-none object-contain"
    />
  )
}

/**
 * Small, non-essential state flourishes. They communicate mood in the same
 * language the copy does, and are hidden from assistive tech (the needs panel
 * carries the real information).
 */
function CreatureOverlay({ state }: { state: CreatureStateId }) {
  let content: ReactNode = null

  switch (state) {
    case 'SLEEPING':
      content = (
        <div className="absolute right-[16%] top-[6%] text-2xl text-cold-400/80">
          <span className="nb-drift absolute inline-block">z</span>
          <span className="nb-drift nb-drift--2 absolute left-4 inline-block text-lg">z</span>
          <span className="nb-drift nb-drift--3 absolute left-8 inline-block text-sm">z</span>
        </div>
      )
      break
    case 'HUNGRY':
      content = (
        <div className="absolute right-[18%] top-[8%] flex h-10 w-10 items-center justify-center rounded-full border border-ash-600/60 bg-night-850/80 text-lg nb-emerge">
          🍖
        </div>
      )
      break
    case 'DIRTY':
      content = (
        <div className="absolute inset-0">
          <span className="absolute left-[30%] top-[38%] h-2 w-2 rounded-full bg-blood-700/70 nb-pulse" />
          <span className="absolute left-[54%] top-[55%] h-1.5 w-1.5 rounded-full bg-rot-600/70 nb-pulse nb-pulse--2" />
          <span className="absolute left-[42%] top-[66%] h-1.5 w-1.5 rounded-full bg-blood-700/60 nb-pulse nb-pulse--3" />
        </div>
      )
      break
    case 'HAPPY':
      content = (
        <div className="absolute inset-0 text-ember-400">
          <span className="nb-twinkle absolute left-[24%] top-[24%] text-sm">✦</span>
          <span className="nb-twinkle nb-twinkle--2 absolute left-[64%] top-[38%] text-xs">✦</span>
        </div>
      )
      break
    case 'INJURED':
      content = (
        <span className="absolute left-[38%] top-[46%] nb-pulse text-lg font-bold text-blood-500">
          ✕
        </span>
      )
      break
    case 'TRAINING':
      content = (
        <div className="absolute inset-0">
          <span className="nb-drip absolute left-[72%] top-[28%] text-xs text-cold-400">❜</span>
          <span className="nb-drip nb-drip--2 absolute left-[82%] top-[36%] text-xs text-cold-400">❜</span>
        </div>
      )
      break
    case 'EATING':
      content = (
        <div className="absolute inset-x-0 bottom-[16%] flex justify-center gap-1 text-[10px] text-bone-300/70">
          <span className="nb-drift">·</span>
          <span className="nb-drift nb-drift--2">·</span>
          <span className="nb-drift nb-drift--3">·</span>
        </div>
      )
      break
    default:
      content = null
  }

  if (!content) return null
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-visible">
      {content}
    </div>
  )
}
